import { useMemo, useState } from 'react'

import { useAction } from '../components/toast'
import { Badge, Card, Loaded, Notice, Page, ReasonDialog, Table } from '../components/ui'
import { get, put } from '../lib/api'
import { P, useAuth } from '../lib/auth'
import { count, money, ms, titleCase } from '../lib/format'
import { useResource } from '../lib/useResource'

type Rule = {
  preferred?: string
  fallbacks?: string[]
  max_cost_usd?: number
  latency_budget_ms?: number
  region?: string
}

type Routing = {
  task_classes: string[]
  rules: Record<string, Rule>
  allowed_models: string[]
  region: string
  redaction_policy: string
  store_prompts: boolean
  known_models: string[]
}

type Model = {
  id: string
  label: string
  provider: string
  input_price_per_mtok: number
  output_price_per_mtok: number
  price_verified: boolean
}

type RequestRow = {
  id: string
  task_class: string
  model_id: string
  input_tokens: number
  output_tokens: number
  cost_usd: number
  latency_ms: number
}

export function AiRouting() {
  const { can } = useAuth()
  const run = useAction()
  const editable = can(P.aiRoutingWrite)

  const routing = useResource((signal) => get<Routing>('/v1/admin/ai/routing', undefined, signal))
  const models = useResource((signal) => get<Model[]>('/v1/admin/ai/models', undefined, signal))
  const history = useResource((signal) =>
    get<RequestRow[]>('/v1/admin/ai/requests', { limit: 500 }, signal),
  )

  const [draft, setDraft] = useState<Record<string, Rule> | null>(null)
  const [confirming, setConfirming] = useState(false)

  const rules = draft ?? routing.data?.rules ?? {}
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(routing.data?.rules ?? {})

  const update = (taskClass: string, patch: Partial<Rule>) => {
    setDraft({ ...rules, [taskClass]: { ...rules[taskClass], ...patch } })
  }

  const priceOf = useMemo(() => {
    const table = new Map((models.data ?? []).map((m) => [m.id, m]))
    return (modelId: string, input: number, output: number): number | null => {
      const spec = table.get(modelId)
      if (!spec) return null
      return (
        (input / 1_000_000) * spec.input_price_per_mtok +
        (output / 1_000_000) * spec.output_price_per_mtok
      )
    }
  }, [models.data])

  /** Replay the observed request log against the draft, re-pricing each request
   *  at the model the draft would have chosen. Token counts are the ones that
   *  actually occurred; only the price changes. Latency is *not* simulated —
   *  see the caveat rendered below. */
  const simulation = useMemo(() => {
    const rows = history.data ?? []
    if (rows.length === 0) return null
    let actual = 0
    let projected = 0
    let repriced = 0
    let unknown = 0
    const perClass = new Map<string, { actual: number; projected: number; n: number }>()

    for (const row of rows) {
      const target = rules[row.task_class]?.preferred ?? row.model_id
      const estimate = priceOf(target, row.input_tokens, row.output_tokens)
      actual += row.cost_usd
      if (estimate === null) {
        unknown += 1
        projected += row.cost_usd
      } else {
        projected += estimate
        if (target !== row.model_id) repriced += 1
      }
      const bucket = perClass.get(row.task_class) ?? { actual: 0, projected: 0, n: 0 }
      bucket.actual += row.cost_usd
      bucket.projected += estimate ?? row.cost_usd
      bucket.n += 1
      perClass.set(row.task_class, bucket)
    }

    return {
      sampled: rows.length,
      rerouted: repriced,
      unknown,
      actual,
      projected,
      perClass: [...perClass.entries()].map(([taskClass, b]) => ({ taskClass, ...b })),
    }
  }, [history.data, rules, priceOf])

  const latencyByModel = useMemo(() => {
    const buckets = new Map<string, number[]>()
    for (const row of history.data ?? []) {
      buckets.set(row.model_id, [...(buckets.get(row.model_id) ?? []), row.latency_ms])
    }
    return new Map(
      [...buckets.entries()].map(([model, values]) => {
        const sorted = values.sort((a, b) => a - b)
        const index = Math.min(Math.floor(sorted.length * 0.95), sorted.length - 1)
        return [model, sorted[index] ?? 0]
      }),
    )
  }, [history.data])

  return (
    <Page
      title="Routing rules"
      subtitle="Which model the gateway reaches for, per task class. The recommender scores candidates against these constraints; a rule that eliminates every model raises NoEligibleModel rather than silently falling through."
      actions={
        dirty ? (
          <>
            <button className="btn btn--sm btn--ghost" onClick={() => setDraft(null)}>
              Discard
            </button>
            <button className="btn btn--sm btn--primary" onClick={() => setConfirming(true)}>
              Save ruleset
            </button>
          </>
        ) : null
      }
    >
      {!editable && (
        <Notice>
          You can read this ruleset but not change it — that needs{' '}
          <span className="mono">ai:routing:write</span>.
        </Notice>
      )}

      <Loaded resource={routing}>
        {(data) => (
          <>
            <div className="row row--wrap">
              <Badge>Region: {data.region}</Badge>
              <Badge tone={data.redaction_policy === 'off' ? 'alert' : 'green'}>
                Redaction: {data.redaction_policy}
              </Badge>
              <Badge tone={data.store_prompts ? 'orange' : undefined}>
                Prompt retention: {data.store_prompts ? 'on' : 'off'}
              </Badge>
            </div>

            <Card flush>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Task class</th>
                      <th>Preferred model</th>
                      <th>Fallback chain</th>
                      <th>Max cost / request</th>
                      <th>Latency budget</th>
                      <th>Observed p95</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.task_classes.map((taskClass) => {
                      const rule = rules[taskClass] ?? {}
                      return (
                        <tr key={taskClass}>
                          <td className="strong">{titleCase(taskClass)}</td>
                          <td>
                            <select
                              className="select"
                              style={{ minWidth: 190 }}
                              disabled={!editable}
                              value={rule.preferred ?? ''}
                              onChange={(event) =>
                                update(taskClass, { preferred: event.target.value || undefined })
                              }
                            >
                              <option value="">Recommender decides</option>
                              {data.known_models.map((id) => (
                                <option key={id} value={id}>
                                  {id}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              className="input"
                              style={{ minWidth: 220 }}
                              disabled={!editable}
                              placeholder="comma-separated model ids"
                              value={(rule.fallbacks ?? []).join(', ')}
                              onChange={(event) =>
                                update(taskClass, {
                                  fallbacks: event.target.value
                                    .split(',')
                                    .map((s) => s.trim())
                                    .filter(Boolean),
                                })
                              }
                            />
                          </td>
                          <td>
                            <input
                              className="input"
                              style={{ width: 110 }}
                              type="number"
                              step="0.001"
                              min="0"
                              disabled={!editable}
                              value={rule.max_cost_usd ?? ''}
                              onChange={(event) =>
                                update(taskClass, {
                                  max_cost_usd: event.target.value
                                    ? Number(event.target.value)
                                    : undefined,
                                })
                              }
                            />
                          </td>
                          <td>
                            <input
                              className="input"
                              style={{ width: 110 }}
                              type="number"
                              step="100"
                              min="0"
                              disabled={!editable}
                              value={rule.latency_budget_ms ?? ''}
                              onChange={(event) =>
                                update(taskClass, {
                                  latency_budget_ms: event.target.value
                                    ? Number(event.target.value)
                                    : undefined,
                                })
                              }
                            />
                          </td>
                          <td className="dim">
                            {rule.preferred && latencyByModel.has(rule.preferred)
                              ? ms(latencyByModel.get(rule.preferred))
                              : '—'}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </Loaded>

      <Card
        title="Simulator"
        hint="Replays the last 500 real requests against the draft ruleset"
      >
        <Loaded resource={history}>
          {() =>
            simulation === null ? (
              <div className="empty">
                No request history to replay yet. The simulator needs real traffic — it does not
                invent a workload.
              </div>
            ) : (
              <div className="stack">
                <Notice>
                  <strong>What this does and does not model.</strong> Token counts are the ones
                  that actually occurred; only the price changes, so the cost delta is arithmetic
                  rather than a guess. Latency is <em>not</em> simulated — a different model has a
                  different latency distribution and replaying old requests cannot tell you what it
                  would have been. The observed p95 column above is measured, not predicted.
                  {simulation.unknown > 0 && (
                    <>
                      {' '}
                      {count(simulation.unknown)} request(s) reference a model with no price in the
                      catalog and were carried at their recorded cost.
                    </>
                  )}
                </Notice>

                <div className="row row--wrap" style={{ gap: 28 }}>
                  <span>
                    <span className="kpi__label">Sampled</span>
                    <div className="num" style={{ fontSize: 22 }}>
                      {count(simulation.sampled)}
                    </div>
                  </span>
                  <span>
                    <span className="kpi__label">Would reroute</span>
                    <div className="num" style={{ fontSize: 22 }}>
                      {count(simulation.rerouted)}
                    </div>
                  </span>
                  <span>
                    <span className="kpi__label">Actual cost</span>
                    <div className="num" style={{ fontSize: 22 }}>
                      {money(simulation.actual)}
                    </div>
                  </span>
                  <span>
                    <span className="kpi__label">Under draft</span>
                    <div
                      className="num"
                      style={{
                        fontSize: 22,
                        color:
                          simulation.projected > simulation.actual
                            ? 'var(--alert-bright)'
                            : 'var(--green)',
                      }}
                    >
                      {money(simulation.projected)}
                    </div>
                  </span>
                  <span>
                    <span className="kpi__label">Delta</span>
                    <div
                      className="num"
                      style={{
                        fontSize: 22,
                        color:
                          simulation.projected > simulation.actual
                            ? 'var(--alert-bright)'
                            : 'var(--green)',
                      }}
                    >
                      {simulation.projected >= simulation.actual ? '+' : '−'}
                      {money(Math.abs(simulation.projected - simulation.actual))}
                    </div>
                  </span>
                </div>

                <Table
                  rows={simulation.perClass}
                  rowKey={(row) => row.taskClass}
                  empty="No traffic to replay."
                  columns={[
                    { key: 'tc', header: 'Task class', strong: true, render: (r) => titleCase(r.taskClass) },
                    { key: 'n', header: 'Requests', render: (r) => count(r.n) },
                    { key: 'a', header: 'Actual', render: (r) => <span className="num">{money(r.actual)}</span> },
                    { key: 'p', header: 'Draft', render: (r) => <span className="num">{money(r.projected)}</span> },
                    {
                      key: 'd',
                      header: 'Delta',
                      render: (r) => (
                        <span
                          className="num"
                          style={{
                            color: r.projected > r.actual ? 'var(--alert-bright)' : 'var(--green)',
                          }}
                        >
                          {r.projected >= r.actual ? '+' : '−'}
                          {money(Math.abs(r.projected - r.actual))}
                        </span>
                      ),
                    },
                  ]}
                />
              </div>
            )
          }
        </Loaded>
      </Card>

      {confirming && draft && (
        <ReasonDialog
          title="Save the routing ruleset"
          description="Routing decides which vendor sees each request. The API rejects any rule naming a model that is not in the catalog."
          confirmLabel="Save ruleset"
          minLength={10}
          onClose={() => setConfirming(false)}
          onConfirm={(reason) =>
            run(
              () => put('/v1/admin/ai/routing', { rules: draft, reason }),
              'Routing rules saved.',
              () => {
                setDraft(null)
                routing.reload()
              },
            )
          }
        />
      )}
    </Page>
  )
}
